import { useState, useEffect, useRef, type ReactNode } from 'react';
import {
  BarChart3,
  ChevronDown,
  History,
  FileSpreadsheet,
  Table2,
  FileDown,
} from 'lucide-react';

export type ListExportFmt = 'csv' | 'xlsx' | 'pdf';

export type ListOperationsExtraAction = {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

/** Reports menu: Object history (new tab) + export (selection / all). Label shown as "Reports". */
export function ListOperationsDropdown(props: {
  /** Opens object history in a new browser tab for this org-scoped entity. */
  objectHistoryHref: string;
  onExport: (fmt: ListExportFmt, scope: 'selection' | 'all') => void;
  disabled?: boolean;
  /** When false, Excel/CSV options are hidden (e.g. viewers); PDF remains if still rendered below. */
  allowSpreadsheetExport?: boolean;
  /** List-specific actions (configure, import, manage rights, etc.) — §2.13 toolbar collapse. */
  extraActions?: ListOperationsExtraAction[];
}) {
  const allowSheets = props.allowSpreadsheetExport !== false;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  /** Compact row: fixed icon tint + hug content (no extra right whitespace). */
  const item = (label: string, icon: ReactNode, fn: () => void) => (
    <button
      type="button"
      className="flex w-full max-w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] font-normal vscode-text-primary hover:vscode-bg-hover"
      onClick={() => {
        fn();
        setOpen(false);
      }}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-[color-mix(in_srgb,var(--vscode-border-primary)_45%,transparent)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_88%,transparent)] [&_svg]:h-3 [&_svg]:w-3">
        {icon}
      </span>
      <span className="min-w-0 whitespace-nowrap leading-tight">{label}</span>
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={props.disabled}
        className="btn-secondary inline-flex shrink-0 items-center gap-1.5"
        onClick={() => setOpen(!open)}
      >
        <BarChart3 className="h-3.5 w-3.5" />
        Reports
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-[120] mt-1 w-max max-w-[min(100vw-1rem,20rem)] rounded-md border vscode-border vscode-bg-elevated py-0.5 shadow-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_50%,transparent)]"
        >
          {props.extraActions && props.extraActions.length > 0 ? (
            <>
              <p className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.06em] vscode-text-tertiary">
                Actions
              </p>
              {props.extraActions.map((ex) => (
                <button
                  key={ex.id}
                  type="button"
                  disabled={ex.disabled}
                  className="flex w-full max-w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] font-normal vscode-text-primary hover:vscode-bg-hover disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => {
                    ex.onClick();
                    setOpen(false);
                  }}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-[color-mix(in_srgb,var(--vscode-border-primary)_45%,transparent)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_88%,transparent)] [&_svg]:h-3 [&_svg]:w-3">
                    {ex.icon}
                  </span>
                  <span className="min-w-0 whitespace-nowrap leading-tight">{ex.label}</span>
                </button>
              ))}
              <div className="my-0.5 border-t vscode-border" />
            </>
          ) : null}
          <button
            type="button"
            className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] font-normal vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              window.open(props.objectHistoryHref, '_blank', 'noopener,noreferrer');
              setOpen(false);
            }}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-violet-500/25 bg-violet-500/10 text-violet-600 dark:text-violet-400">
              <History className="h-3 w-3" strokeWidth={2} />
            </span>
            <span className="min-w-0 whitespace-nowrap leading-tight">Object History</span>
          </button>
          {allowSheets ? (
            <>
              <div className="my-0.5 border-t vscode-border" />
              <p className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.06em] vscode-text-tertiary">
              Export (selection)
              </p>
              {item(
                'Excel · xlsx',
                <FileSpreadsheet className="text-emerald-600 dark:text-emerald-400" strokeWidth={2} />,
                () => props.onExport('xlsx', 'selection'),
              )}
              {item('CSV', <Table2 className="text-sky-600 dark:text-sky-400" strokeWidth={2} />, () =>
                props.onExport('csv', 'selection'),
              )}
              <div className="my-0.5 border-t vscode-border" />
              <p className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.06em] vscode-text-tertiary">
              Export all (list)
              </p>
              {item(
                'Excel — all',
                <FileSpreadsheet className="text-emerald-600 dark:text-emerald-400" strokeWidth={2} />,
                () => props.onExport('xlsx', 'all'),
              )}
              {item('CSV — all', <Table2 className="text-sky-600 dark:text-sky-400" strokeWidth={2} />, () =>
                props.onExport('csv', 'all'),
              )}
            </>
          ) : null}
          <div className="my-0.5 border-t vscode-border" />
          <p className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.06em] vscode-text-tertiary">Print / PDF</p>
          {item(
            'PDF — selection',
            <FileDown className="text-red-600 dark:text-red-400" strokeWidth={2} />,
            () => props.onExport('pdf', 'selection'),
          )}
          {item('PDF — all', <FileDown className="text-red-600 dark:text-red-400" strokeWidth={2} />, () =>
            props.onExport('pdf', 'all'),
          )}
        </div>
      )}
    </div>
  );
}
